/**
 * Consent Studio -> Shopify Customer Privacy API bridge.
 *
 * Consent Studio (loaded from `layout/theme.liquid`) is the store's CMP: it owns
 * the cookie banner and the visitor's choice per category (functional /
 * analytics / marketing). Shopify keeps its own consent record, which gates
 * Shopify analytics, customer events and every marketing web pixel. Without a
 * bridge the two never talk: the visitor accepts cookies in the Consent Studio
 * banner while Shopify still considers tracking denied.
 *
 * This script forwards every Consent Studio decision to
 * `Shopify.customerPrivacy.setTrackingConsent()`. Downstream of that record sit
 * Shopify's own analytics and the store's custom web pixel, which subscribes to
 * `api.customerPrivacy` and only loads GTM once consent has been synced — so an
 * unset record means the measurement stack never starts.
 *
 * Consent Studio can also inject this same call itself (dashboard ->
 * integrations, enabled for the kamera-express.nl site config only — the .be and
 * .de configs have no integration). That injected version checks whether the API
 * exists at the moment the banner resolves and silently drops the consent if it
 * doesn't, which is a real race: verified on the playground store, a decision
 * made while `consent-tracking-api` was still loading never reached Shopify.
 * This bridge queues the last known state instead and flushes it once the API
 * answers, and it runs on every market domain regardless of dashboard
 * configuration. Calls are deduplicated against Shopify's current record, so
 * having both active costs nothing.
 *
 * Category mapping (mirrors Consent Studio's own integration):
 *   functional -> preferences
 *   analytics  -> analytics
 *   marketing  -> marketing + sale_of_data
 */

/** Consent Studio dispatches these on `window`. Names are derived from its cookie prefix. */
const CS_EVENT_INIT = 'consent-studio__init';
const CS_EVENT_EMPTY = 'consent-studio__empty';
const CS_EVENT_UPDATED = 'consent-studio__updated';

/** Keys of a Shopify tracking-consent payload. */
const CONSENT_KEYS = ['preferences', 'analytics', 'marketing', 'sale_of_data'];

/**
 * Categories that count as granted before the visitor has answered the banner.
 * Matches both Consent Studio's own defaults and the Google Consent Mode
 * defaults in `theme.liquid`, where `functionality_storage` starts granted.
 * Once a choice is stored it replaces this entirely — a visitor who rejects
 * functional cookies gets `preferences: false`.
 */
const IMPLICIT_CATEGORIES = ['functional'];

/** Interval between attempts to reach a not-yet-available Shopify API. */
const RETRY_INTERVAL = 250;

/** How long to wait for Shopify's feature loader before giving up (25 * 250ms). */
const LOAD_FEATURES_MAX_ATTEMPTS = 25;

/** How long a queued decision keeps waiting for the API (120 * 250ms = 30s). */
const FLUSH_MAX_ATTEMPTS = 120;

/**
 * Last state that still has to reach Shopify. Kept until the Customer Privacy
 * API is available, so a decision made before it loads is never lost.
 * @type {Record<string, boolean> | null}
 */
let pendingConsent = null;

let privacyApiRequested = false;
let loadFeaturesAttempts = 0;
let flushAttempts = 0;

/** @type {ReturnType<typeof setTimeout> | null} */
let flushTimer = null;

/**
 * Signature of the last state handed to Shopify. Consent Studio can announce the
 * same decision more than once (initial sync, `__init`, `__updated`), and
 * `currentVisitorConsent()` still reports the old value while a call is in
 * flight — so remember what we sent rather than asking after the fact.
 * @type {string | null}
 */
let lastSentSignature = null;

/* -------------------------------------------------------------------------- */
/* Reading Consent Studio                                                     */
/* -------------------------------------------------------------------------- */

/**
 * The categories the visitor has granted, read straight from Consent Studio.
 * Returns null when Consent Studio has not loaded (unregistered domain, blocked
 * request) — in that case we must not touch Shopify's own consent record.
 * @returns {string[] | null}
 */
function grantedCategories() {
  const consentStudio = window.consentStudio;
  if (!consentStudio) return null;

  const stored = consentStudio.api?.consentState?.get?.();
  if (Array.isArray(stored)) return stored;

  const cached = consentStudio.getCachedConsent?.();
  if (Array.isArray(cached)) return cached;

  // Loaded, but the visitor has not answered the banner yet.
  return IMPLICIT_CATEGORIES;
}

/**
 * @param {string[]} categories
 * @returns {Record<string, boolean>} Shopify tracking-consent payload.
 */
function toConsentState(categories) {
  const marketing = categories.includes('marketing');

  return {
    preferences: categories.includes('functional'),
    analytics: categories.includes('analytics'),
    marketing: marketing,
    sale_of_data: marketing,
  };
}

/* -------------------------------------------------------------------------- */
/* Writing to Shopify                                                         */
/* -------------------------------------------------------------------------- */

/**
 * Normalises a value from `currentVisitorConsent()` — Shopify answers with
 * 'yes' / 'no' / '' (no decision recorded), older versions with booleans.
 * @param {unknown} value
 * @returns {boolean | null} null when no decision is recorded.
 */
function normaliseConsentValue(value) {
  if (value === true || value === 'yes') return true;
  if (value === false || value === 'no') return false;
  return null;
}

/**
 * @param {Record<string, boolean>} state
 * @returns {string} Comparable signature of a consent payload.
 */
function signatureOf(state) {
  return CONSENT_KEYS.map((key) => (state[key] ? '1' : '0')).join('');
}

/**
 * True when Shopify already holds exactly this state, so we can skip the call.
 * Also covers the case where Consent Studio's own dashboard integration is
 * enabled and got there first.
 * @param {any} privacy `Shopify.customerPrivacy`
 * @param {Record<string, boolean>} state
 */
function matchesCurrentConsent(privacy, state) {
  const current = privacy.currentVisitorConsent?.();
  if (!current) return false;

  return CONSENT_KEYS.every((key) => normaliseConsentValue(current[key]) === state[key]);
}

/**
 * Retries `flushConsent` while the Customer Privacy API is still unavailable.
 * The `loadFeatures` callback alone is not enough: it fires once per page, so a
 * decision made after it resolved but before the API object exists — or one made
 * during a slow load — would otherwise sit in the queue forever.
 */
function scheduleFlush() {
  if (flushTimer !== null || flushAttempts >= FLUSH_MAX_ATTEMPTS) return;

  flushAttempts += 1;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flushConsent();
  }, RETRY_INTERVAL);
}

/** Sends `pendingConsent` to Shopify, or leaves it queued if the API isn't ready. */
function flushConsent() {
  if (!pendingConsent) return;

  const privacy = window.Shopify?.customerPrivacy;
  if (typeof privacy?.setTrackingConsent !== 'function') {
    requestPrivacyApi();
    scheduleFlush();
    return;
  }

  const state = pendingConsent;
  pendingConsent = null;

  const signature = signatureOf(state);
  if (signature === lastSentSignature) return;
  if (matchesCurrentConsent(privacy, state)) return;

  lastSentSignature = signature;
  privacy.setTrackingConsent(state, (result) => {
    if (result?.error) {
      // Allow a later announcement of the same decision to try again.
      lastSentSignature = null;
      console.error('[APC] Consent Studio: Shopify rejected the tracking consent', result.error);
    }
  });
}

/**
 * Loads Shopify's `consent-tracking-api` feature once, then flushes. The feature
 * loader itself ships with `content_for_header`, so it can still be missing when
 * this script runs — poll briefly rather than losing the consent.
 */
function requestPrivacyApi() {
  if (privacyApiRequested) return;

  const loadFeatures = window.Shopify?.loadFeatures;
  if (typeof loadFeatures !== 'function') {
    if (loadFeaturesAttempts >= LOAD_FEATURES_MAX_ATTEMPTS) return;
    loadFeaturesAttempts += 1;
    setTimeout(requestPrivacyApi, RETRY_INTERVAL);
    return;
  }

  privacyApiRequested = true;
  loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
    if (error) {
      console.error('[APC] Consent Studio: could not load Shopify consent-tracking-api', error);
      return;
    }
    flushConsent();
  });
}

/* -------------------------------------------------------------------------- */
/* Theme-side consent contract                                                */
/* -------------------------------------------------------------------------- */

/**
 * Mirrors the state onto `window.apcConsent` and announces marketing consent,
 * the contract theme scripts use to unblock third parties (see `apc-gpt.js`).
 * @param {string[]} categories
 * @param {Record<string, boolean>} state
 */
function exposeThemeConsent(categories, state) {
  const previous = window.apcConsent;

  window.apcConsent = {
    functional: state.preferences,
    analytics: state.analytics,
    marketing: state.marketing,
    categories: categories,
  };

  if (state.marketing && previous?.marketing !== true) {
    window.dispatchEvent(new CustomEvent('apc:marketing-consent-granted'));
  }
}

/* -------------------------------------------------------------------------- */
/* Bootstrap                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * @param {string[]} categories
 */
function publishConsent(categories) {
  const state = toConsentState(categories);

  pendingConsent = state;
  flushAttempts = 0;
  exposeThemeConsent(categories, state);
  flushConsent();
}

/** Reads whatever Consent Studio holds right now — covers repeat visits. */
function syncFromConsentStudio() {
  const categories = grantedCategories();
  if (categories === null) return;

  publishConsent(categories);
}

window.addEventListener(CS_EVENT_UPDATED, (event) => {
  const detail = /** @type {CustomEvent} */ (event).detail;
  publishConsent(detail?.categories || grantedCategories() || []);
});

// Banner loaded with nothing stored yet: record the denial explicitly so
// Shopify blocks tracking instead of falling back to its own defaults.
window.addEventListener(CS_EVENT_EMPTY, () => publishConsent(IMPLICIT_CATEGORIES));

// Consent Studio may boot before or after this script; handle both.
window.addEventListener(CS_EVENT_INIT, syncFromConsentStudio);
syncFromConsentStudio();
